/* import __COLOCATED_TEMPLATE__ from './overview.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
/* This file needs to be refactored to use the Generic integration UI. For now, we're disabling the direct platform access errors. */
/* eslint-disable @intercom/intercom/no-direct-zendesk-platform-access */
/* eslint-disable @intercom/intercom/no-direct-salesforce-platform-access */
/* eslint-disable @intercom/intercom/no-direct-hubspot-platform-access */
/* eslint-disable @intercom/intercom/no-direct-freshdesk-platform-access */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { type Channel } from '../deploy/overview/channel-options';
import { assetUrl } from '@intercom/pulse/helpers/asset-url';
import type IntlService from 'ember-intl/services/intl';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import {
  NEW_API_CONVERSATION_TARGET,
  NEW_CONVERSATION_TARGET,
  NEW_ZENDESK_SUNSHINE_CONVERSATION_TARGET,
  NEW_ZENDESK_TICKET_TARGET,
  NEW_SALESFORCE_CASE_TARGET,
} from 'embercom/lib/operator/custom-bots/constants';
import type FinStandaloneIntegrationsService from 'embercom/services/fin-standalone-integrations-service';
import { objectTypes } from 'embercom/models/data/matching-system/matching-constants';
import StandaloneIntercomMessengerPill from 'embercom/components/standalone/intercom-messenger/deploy/pill';
import StandaloneZendeskTicketsPill from 'embercom/components/standalone/zendesk/deploy/tickets/pill';
import StandaloneZendeskSunshinePill from 'embercom/components/standalone/zendesk/deploy/sunshine/pill';
import StandaloneSalesforceCasesPill from 'embercom/components/standalone/deploy/salesforce/cases/pill';
import type Store from '@ember-data/store';
import type ApiConfiguration from 'embercom/models/standalone/api-configuration';
import { IntegrationState, StandalonePlatform } from 'embercom/objects/standalone/constants';
import StandaloneCopilotSummaryCard from './overview/copilot-summary';
import type RegionService from 'embercom/services/region-service';

interface Signature {
  Element: HTMLDivElement;
  Args: {};
}

export default class StandaloneDeployOverview extends Component<Signature> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare appService: {
    app: {
      canUseFeature: (feature: string) => boolean;
      id: string;
      canUseCopilotOnStandalone: boolean;
      canStandalonePlatformAccessReactIntegrationPages: boolean;
      hasStandalonePlatform: (platform: StandalonePlatform) => boolean;
    };
  };
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare regionService: RegionService;
  @service declare finStandaloneIntegrationsService: FinStandaloneIntegrationsService;

  get genericPlatforms() {
    return this.appService.app.canStandalonePlatformAccessReactIntegrationPages;
  }

  get availableFallbackChannels(): Channel[] {
    let availableChannels: Channel[] = [
      {
        // Intercom Messenger channel
        icon: 'messenger',
        title: this.intl.t('standalone.intercom-messenger.messenger-card-title'),
        externalTypeName: 'conversation',
        integrationState: this.finStandaloneService.intercomMessengerConfig.integrationState,
        setupArgs: {
          description: this.intl.t(
            'standalone.intercom-messenger.messenger-card-description-generic',
          ),
          route: 'apps.app.standalone.deploy.intercom-messenger',
          imageAssetUrl: assetUrl('/assets/images/standalone/ic-messenger.jpg'),
          sectionTitle: this.intl.t(
            'standalone.deploy.overview.generic.let-fin-answer-your-live-chats',
          ),
        },
        summaryArgs: {
          workflowRoute: 'apps.app.standalone.deploy.intercom-messenger.workflow.show',
          manageRoute: 'apps.app.standalone.deploy.intercom-messenger.go-live',
          statusPill: StandaloneIntercomMessengerPill,
          workflowParams: {
            target: NEW_CONVERSATION_TARGET,
            objectType: objectTypes.inboundCustomBot,
          },
        },
      },
    ];

    if (this.finOverApiChannel) {
      availableChannels.push(this.finOverApiChannel);
    }

    return availableChannels;
  }

  get availableZendeskChannels(): Channel[] {
    let availableChannels: Channel[] = [
      {
        // Intercom Messenger channel
        icon: 'messenger',
        title: this.intl.t('standalone.intercom-messenger.messenger-card-title'),
        externalTypeName: 'conversation',
        integrationState: this.finStandaloneService.intercomMessengerConfig.integrationState,
        setupArgs: {
          description: this.intl.t(
            'standalone.intercom-messenger.messenger-card-description-for-zendesk',
          ),
          route: 'apps.app.standalone.deploy.intercom-messenger',
          imageAssetUrl: assetUrl('/assets/images/standalone/ic-messenger.jpg'),
          sectionTitle: this.intl.t('standalone.deploy.overview.zendesk.setup-chat-channels'),
          articleId: 10725654,
          articleUrl:
            'https://fin.ai/help/en/articles/10725654-fin-in-the-intercom-messenger-how-does-it-work',
        },
        summaryArgs: {
          workflowRoute: 'apps.app.standalone.deploy.intercom-messenger.workflow.show',
          manageRoute: 'apps.app.standalone.deploy.intercom-messenger.go-live',
          statusPill: StandaloneIntercomMessengerPill,
          workflowParams: {
            target: NEW_CONVERSATION_TARGET,
            objectType: objectTypes.inboundCustomBot,
          },
        },
      },
      {
        // Zendesk Messaging channel
        icon: 'zendesk-messenger',
        title: this.intl.t('standalone.channels.overview.cards.zendesk-messaging.title'),
        externalTypeName: 'conversation',
        integrationState: this.finStandaloneService.zendeskPlatform.zendeskConfig
          ?.sunshineIntegration
          ? this.finStandaloneService.zendeskPlatform.zendeskConfig.sunshineIntegration
              .sunshineMessengerState
          : IntegrationState.NotConfigured,
        setupArgs: {
          description: this.intl.t(
            'standalone.channels.overview.cards.zendesk-messaging.description',
          ),
          route: 'apps.app.standalone.deploy.zendesk.messaging',
          imageAssetUrl: assetUrl('/assets/images/standalone/zd-messenger.jpg'),
          sectionTitle: this.intl.t('standalone.deploy.overview.zendesk.setup-chat-channels'),
          articleId: 10729663,
          articleUrl:
            'https://fin.ai/help/en/articles/10729663-fin-for-zendesk-messaging-how-does-it-work',
        },
        summaryArgs: {
          workflowRoute: 'apps.app.standalone.deploy.zendesk.messaging.workflow.show',
          manageRoute: 'apps.app.standalone.deploy.zendesk.messaging.go-live',
          statusPill: StandaloneZendeskSunshinePill,
          workflowParams: {
            target: NEW_ZENDESK_SUNSHINE_CONVERSATION_TARGET,
            objectType: objectTypes.triggerableCustomBot,
          },
        },
      },
      {
        // Zendesk Tickets channel
        icon: 'messenger-ticket-filled',
        title: this.intl.t('standalone.channels.overview.cards.zendesk-tickets.title'),
        externalTypeName: 'ticket',
        integrationState: this.finStandaloneService.zendeskPlatform.zendeskConfig!.ticketState,
        setupArgs: {
          description: this.intl.t(
            'standalone.channels.overview.cards.zendesk-tickets.description',
          ),
          route: 'apps.app.standalone.deploy.zendesk.tickets',
          imageAssetUrl: assetUrl('/assets/images/standalone/zd-tickets.jpg'),
          sectionTitle: this.intl.t('standalone.deploy.overview.zendesk.setup-ticket-channels'),
          articleId: 10729234,
          articleUrl:
            'https://fin.ai/help/en/articles/10729234-fin-for-zendesk-tickets-how-does-it-work',
        },
        summaryArgs: {
          workflowRoute: 'apps.app.standalone.deploy.zendesk.tickets.workflow.show',
          manageRoute: 'apps.app.standalone.deploy.zendesk.tickets.go-live',
          statusPill: StandaloneZendeskTicketsPill,
          workflowParams: {
            target: NEW_ZENDESK_TICKET_TARGET,
            objectType: objectTypes.triggerableCustomBot,
          },
        },
      },
    ];

    if (this.appService.app.canUseCopilotOnStandalone) {
      availableChannels.push({
        icon: 'inbox',
        title: this.intl.t('standalone.channels.overview.cards.zendesk-copilot.title'),
        externalTypeName: 'conversation',
        setupArgs: {
          description: this.intl.t(
            'standalone.channels.overview.cards.zendesk-copilot.description',
          ),
          route: 'apps.app.standalone.deploy.zendesk.copilot',
          imageAssetUrl: assetUrl('/assets/images/standalone/copilot.png'),
          sectionTitle: this.intl.t('standalone.deploy.overview.zendesk.copilot.title'),
        },
        integrationState: this.finStandaloneService.zendeskPlatform.zendeskConfig!
          .hasFinishedCopilotSetup
          ? IntegrationState.Enabled
          : IntegrationState.NotConfigured,
        summaryComponent: StandaloneCopilotSummaryCard,
      });
    }

    if (this.finOverApiChannel) {
      availableChannels.push(this.finOverApiChannel);
    }

    return availableChannels;
  }

  get availableChannels(): Channel[] {
    let channels: Channel[] = this.finStandaloneIntegrationsService.overviewItems;

    if (!channels) {
      return [];
    }
    if (this.finOverApiChannel) {
      channels.push(this.finOverApiChannel);
    }

    return channels;
  }

  get finOverApiChannel(): Channel | undefined {
    if (this.appService.app.canUseFeature('team-standalone-fin-over-api')) {
      let apiConfig = this.store.peekRecord(
        'standalone/api-configuration',
        this.appService.app.id,
      ) as ApiConfiguration;
      return {
        // Fin over API channel
        icon: 'code',
        title: this.intl.t('standalone.deploy.api.title'),
        externalTypeName: 'fin-over-api',
        integrationState: apiConfig.integrationState,
        setupArgs: {
          description: this.intl.t('standalone.deploy.api.description'),
          route: 'apps.app.standalone.deploy.api',
          imageAssetUrl: assetUrl('/assets/images/standalone/fin-over-api.png'),
          sectionTitle: this.intl.t(
            'standalone.deploy.overview.generic.let-fin-answer-your-live-chats',
          ),
        },
        summaryArgs: {
          workflowRoute: 'apps.app.standalone.deploy.api.workflow',
          manageRoute: 'apps.app.standalone.deploy.api',
          workflowParams: {
            target: NEW_API_CONVERSATION_TARGET,
            objectType: objectTypes.triggerableCustomBot,
          },
        },
      };
    }

    return undefined;
  }

  get availableSalesforceChannels(): Channel[] {
    let availableChannels: Channel[] = [
      {
        // Intercom Messenger channel
        icon: 'messenger',
        title: this.intl.t('standalone.intercom-messenger.messenger-card-title'),
        externalTypeName: 'conversation',
        integrationState: this.finStandaloneService.intercomMessengerConfig.integrationState,
        setupArgs: {
          description: this.intl.t(
            'standalone.intercom-messenger.messenger-card-description-for-salesforce',
          ),
          route: 'apps.app.standalone.deploy.intercom-messenger',
          imageAssetUrl: assetUrl('/assets/images/standalone/ic-messenger.jpg'),
          sectionTitle: this.salesforceFinChannelsDeployHeader,
          articleId: 10725654,
          articleUrl:
            'https://fin.ai/help/en/articles/10725654-fin-in-the-intercom-messenger-how-does-it-work',
        },
        summaryArgs: {
          workflowRoute: 'apps.app.standalone.deploy.intercom-messenger.workflow.show',
          manageRoute: 'apps.app.standalone.deploy.intercom-messenger.go-live',
          statusPill: StandaloneIntercomMessengerPill,
          workflowParams: {
            target: NEW_CONVERSATION_TARGET,
            objectType: objectTypes.inboundCustomBot,
          },
        },
      },
      {
        // Salesforce cases channel
        icon: 'salesforce-case',
        title: this.intl.t('standalone.channels.overview.cards.salesforce-cases.title'),
        externalTypeName: 'ticket',
        integrationState: this.finStandaloneService.salesforcePlatform.salesforceConfig!.status,
        setupArgs: {
          description: this.intl.t(
            'standalone.channels.overview.cards.salesforce-cases.description',
          ),
          route: 'apps.app.standalone.deploy.salesforce.cases',
          imageAssetUrl: assetUrl('/assets/images/standalone/zd-tickets.jpg'),
          sectionTitle: this.salesforceFinChannelsDeployHeader,
          articleId: 10614208,
          articleUrl: 'https://fin.ai/help/en/articles/10614208-set-up-fin-for-salesforce-cases',
        },
        summaryArgs: {
          workflowRoute: 'apps.app.standalone.deploy.salesforce.cases.workflow.show',
          manageRoute: 'apps.app.standalone.deploy.salesforce.cases.go-live',
          statusPill: StandaloneSalesforceCasesPill,
          workflowParams: {
            target: NEW_SALESFORCE_CASE_TARGET,
            objectType: objectTypes.triggerableCustomBot,
          },
        },
      },
    ];

    if (this.appService.app.canUseFeature('team-standalone-deploy-phone')) {
      availableChannels.push(this.finOverPhoneChannel);
    }

    if (this.appService.app.canUseCopilotOnStandalone) {
      availableChannels.push({
        icon: 'inbox',
        title: this.intl.t('standalone.channels.overview.cards.salesforce-copilot.title'),
        externalTypeName: 'conversation',
        setupArgs: {
          description: this.intl.t(
            'standalone.channels.overview.cards.salesforce-copilot.description',
          ),
          route: 'apps.app.standalone.deploy.salesforce.copilot',
          imageAssetUrl: assetUrl('/assets/images/standalone/copilot.png'),
          sectionTitle: this.intl.t('standalone.deploy.overview.salesforce.copilot.title'),
        },
        integrationState: this.finStandaloneService.salesforcePlatform.salesforceConfig!
          .hasFinishedCopilotSetup
          ? IntegrationState.Enabled
          : IntegrationState.NotConfigured,
        summaryComponent: StandaloneCopilotSummaryCard,
      });
    }

    return availableChannels;
  }

  get finOverPhoneChannel(): Channel {
    return {
      // Fin over phone channel
      icon: 'phone',
      title: this.intl.t('standalone.channels.overview.cards.phone.title'),
      externalTypeName: 'phone',
      integrationState: IntegrationState.NotConfigured,
      setupArgs: {
        description: this.intl.t('standalone.channels.overview.cards.phone.description'),
        route: 'apps.app.phone.standalone.deploy',
        imageAssetUrl: assetUrl('/assets/images/standalone/phone.jpg'),
        sectionTitle: this.appService.app.hasStandalonePlatform(StandalonePlatform.Salesforce)
          ? this.salesforceFinChannelsDeployHeader
          : undefined,
      },
    };
  }

  get salesforceFinChannelsDeployHeader(): string | undefined {
    if (!this.appService.app.canUseCopilotOnStandalone) {
      return undefined;
    }

    return this.intl.t('standalone.deploy.overview.salesforce.fin-for-salesforce.title');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Deploy::Overview': typeof StandaloneDeployOverview;
    'standalone/deploy/overview': typeof StandaloneDeployOverview;
  }
}
